import { Collapse as TiptapCollapse, type CollapseOptions } from "@corca/tiptap";
import { NodeSelection, Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { Mapping } from "@tiptap/pm/transform";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import { toProsemirrorHotkey } from "@/utils/hotkeys";

import {
  closestCollapsedBlock,
  collapsedBlockSpannedBy,
  collapsedRunInfo,
  expandBlock,
  expandRunHead,
} from "./blockCollapse";
import {
  buildCollapseRunDecorations,
  collapseDecorationsPluginKey,
  type CollapsedSectionInfo,
} from "./buildCollapseRunDecorations";
import { BYPASS_COLLAPSE_SELECTION_GUARD } from "./collapseCopy";
import { expandSection, closestCollapsedSection } from "./sectionsCollapse";
import {
  collectToggleTargets,
  isBlockCollapsible,
  isCollapsed,
  isCollapsible,
  isSectionCollapsible,
} from "./utils";

import type { Transaction } from "@tiptap/pm/state";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    collapse: {
      /**
       * Set the collapsed state of the block-collapse node at `pos`.
       */
      setCollapsed: (pos: number | number[], collapsed: boolean) => ReturnType;
      /**
       * Flip the collapsed state of the block-collapse node at `pos`.
       */
      toggleCollapsed: (pos: number, includeRange?: boolean) => ReturnType;
    };
  }
}

const collapsePluginKey = new PluginKey("blockCollapse");
const collapseGuardPluginKey = new PluginKey("blockCollapseGuard");

export const Collapse = TiptapCollapse.extend<CollapseOptions>({
  addCommands() {
    return {
      setCollapsed:
        (pos, collapsed) =>
        ({ tr, dispatch }) => {
          const positions = Array.isArray(pos) ? pos : [pos];
          const applied: number[] = [];

          positions.forEach((nodePos) => {
            const node = tr.doc.nodeAt(nodePos);

            if (!node || !isCollapsible(node)) {
              return;
            }

            applied.push(nodePos);

            if (dispatch) {
              tr.setNodeMarkup(nodePos, undefined, { ...node.attrs, collapsed });
            }
          });

          if (dispatch && applied.length > 0) {
            if (applied.length > 1) {
              const first = applied[0];
              const last = applied[applied.length - 1];
              const end = last + (tr.doc.nodeAt(last)?.nodeSize ?? 0);

              tr.setSelection(TextSelection.create(tr.doc, first, end));
            } else if (collapsed) {
              const only = applied[0];
              const node = tr.doc.nodeAt(only);
              const { from, to } = tr.selection;

              if (node && from >= only && to <= only + node.nodeSize) {
                tr.setSelection(NodeSelection.create(tr.doc, only));
              }
            }
          }

          return applied.length > 0;
        },

      toggleCollapsed:
        (pos, includeRange) =>
        ({ commands, state }) => {
          const { selection, doc } = state;
          const node = state.doc.nodeAt(pos);

          if (!node || !isCollapsible(node)) {
            return false;
          }

          const nodeEnd = pos + node.nodeSize;
          const inRange =
            includeRange && selection.ranges.some((r) => pos < r.$to.pos && nodeEnd > r.$from.pos);

          if (inRange) {
            const nodes = collectToggleTargets(selection, doc);

            if (nodes.length > 0) {
              const collapsed = !nodes[0].node.attrs.collapsed;
              return commands.setCollapsed(
                nodes.map(({ pos }) => pos),
                collapsed,
              );
            }

            return false;
          }

          return commands.setCollapsed(pos, !isCollapsed(node));
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      [toProsemirrorHotkey("collapse-block")]: () => {
        const { selection, doc } = this.editor.state;

        if (!selection.empty) {
          const nodes = collectToggleTargets(selection, doc);

          if (nodes.length > 0) {
            const collapsed = !nodes[0].node.attrs.collapsed;
            return this.editor.commands.setCollapsed(
              nodes.map(({ pos }) => pos),
              collapsed,
            );
          }

          return false;
        }

        if (selection instanceof NodeSelection) {
          return this.editor.commands.toggleCollapsed(selection.from);
        }

        const { $from } = selection;
        if ($from.depth > 0) {
          return this.editor.commands.toggleCollapsed($from.before(1));
        }

        return false;
      },
    };
  },

  addProseMirrorPlugins() {
    const { editor } = this;

    return [
      new Plugin({
        key: collapsePluginKey,
        props: {
          handleDOMEvents: {
            beforeinput(view, event) {
              if (event.inputType !== "insertText" || event.data == null) {
                return false;
              }

              const { selection } = view.state;

              if (!(selection instanceof TextSelection) || selection.empty) {
                return false;
              }

              const run = collapsedRunInfo(view, selection);

              if (!run) {
                return false;
              }

              event.preventDefault();
              return expandRunHead(editor, run.first, event.data);
            },
          },
          handleClickOn(view, pos, node, nodePos, event) {
            if (event.button === 2) {
              return false;
            }

            if (isCollapsed(node)) {
              if (isSectionCollapsible(node)) {
                return expandSection(editor, nodePos, { caretPos: pos });
              }

              if (isBlockCollapsible(node)) {
                return expandBlock(editor, nodePos);
              }
            }

            const found = closestCollapsedBlock(view.state.doc.resolve(pos));

            if (found) {
              return expandBlock(editor, found.pos);
            }

            return false;
          },

          handleTextInput(view, _from, _to, text) {
            const run = collapsedRunInfo(view, view.state.selection);

            if (!run) {
              return false;
            }

            return expandRunHead(editor, run.first, text);
          },

          handlePaste(view) {
            const section = closestCollapsedSection(view.state.selection);

            if (section) {
              expandSection(editor, section.pos);
            } else {
              const run = collapsedRunInfo(view, view.state.selection);

              if (run) {
                expandRunHead(editor, run.first);
              }
            }

            return false;
          },
        },

        /**
         * If a text caret/range still ends up inside (or spanning) a collapsed block,
         * change it to a `NodeSelection` so the caret can't live in hidden content.
         */
        appendTransaction(transactions, _oldState, newState) {
          if (transactions.some((tr) => tr.getMeta(BYPASS_COLLAPSE_SELECTION_GUARD))) {
            return null;
          }

          const { selection } = newState;

          if (!(selection instanceof TextSelection)) {
            return null;
          }

          const inside = closestCollapsedBlock(selection.$from);
          const containedByInside = inside && selection.to <= inside.pos + inside.node.nodeSize;
          const found = containedByInside ? inside : collapsedBlockSpannedBy(selection);

          if (!found) {
            return null;
          }

          return newState.tr.setSelection(NodeSelection.create(newState.doc, found.pos));
        },
      }),

      /**
       * Stop a collapsed section from silently swallowing content.
       * If an edit (e.g. demoting or deleting the next heading)
       * makes a collapsed head hide more than before, expand it.
       */
      new Plugin({
        key: collapseGuardPluginKey,
        appendTransaction(transactions, oldState, newState) {
          if (!transactions.some((tr) => tr.docChanged && touchesBlockStructure(tr))) {
            return null;
          }

          const before = collapseDecorationsPluginKey.getState(oldState)?.sections;

          if (!before || before.size === 0) {
            return null;
          }

          const after = collapseDecorationsPluginKey.getState(newState)?.sections;

          if (!after) {
            return null;
          }

          const mapping = new Mapping();
          transactions.forEach((tr) => mapping.appendMapping(tr.mapping));

          const toExpand: number[] = [];

          before.forEach(({ hidden: hiddenBefore }, headPos) => {
            const mapped = mapping.mapResult(headPos);

            if (mapped.deleted) {
              return;
            }

            const hiddenAfter = after.get(mapped.pos)?.hidden;

            if (hiddenAfter !== undefined && hiddenAfter > hiddenBefore) {
              toExpand.push(mapped.pos);
            }
          });

          if (toExpand.length === 0) {
            return null;
          }

          const { tr } = newState;

          toExpand.forEach((pos) => {
            const node = tr.doc.nodeAt(pos);

            if (node) {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, collapsed: false });
            }
          });

          queueMicrotask(() => {
            const { node } = editor.view.domAtPos(editor.view.state.selection.from);
            const element = node instanceof Element ? node : node.parentElement;

            element?.scrollIntoView({ block: "center" });
          });

          return tr;
        },
      }),

      new Plugin({
        key: collapseDecorationsPluginKey,
        state: {
          init: (_config, { doc }) => buildCollapseRunDecorations(doc),
          apply: (tr, value) => {
            if (!tr.docChanged) {
              return value;
            }

            /**
             * Rebuild from scratch only when the collapse structure could have changed:
             * - a collapse toggle
             * - blocks added/removed/split/merged
             *
             * Otherwise it's a plain text edit and the structure is unchanged,
             * positions just shifted — so we keep the cached state and re-map positions of:
             * - decorations
             * - each section's head key and its `end` (the `hidden` count is unchanged)
             */
            if (touchesBlockStructure(tr)) {
              return buildCollapseRunDecorations(tr.doc);
            }

            const sections = new Map<number, CollapsedSectionInfo>();

            value.sections.forEach(({ hidden, end }, pos) => {
              const mapped = tr.mapping.mapResult(pos);
              if (!mapped.deleted) {
                sections.set(mapped.pos, { hidden, end: tr.mapping.map(end, -1) });
              }
            });

            return {
              decorations: value.decorations.map(tr.mapping, tr.doc),
              sections,
            };
          },
        },
        props: {
          decorations(state) {
            const hidden =
              collapseDecorationsPluginKey.getState(state)?.decorations ?? DecorationSet.empty;
            const { selection } = state;

            if (selection.empty) {
              return hidden;
            }

            const highlights: Decoration[] = [];

            state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
              if (isBlockCollapsible(node) && isCollapsed(node)) {
                highlights.push(
                  Decoration.node(pos, pos + node.nodeSize, { class: "collapse-selected" }),
                );
                return false;
              }
              return true;
            });

            return highlights.length ? hidden.add(state.doc, highlights) : hidden;
          },
        },
      }),
    ];
  },
});

/**
 * Did this transaction change the document's block structure — blocks added, removed, split, or
 * merged, or a node replaced via `setNodeMarkup` (which is how both a collapse toggle and a heading
 * level change work) — rather than just editing text, which only shifts positions around?
 *
 * We ask so we know when to rebuild the collapse decorations from scratch instead of cheaply
 * re-mapping the existing ones to their new positions.
 *
 * One case slips through on purpose: changing only a node's attribute (a block's alignment, say)
 * doesn't move anything, so its step reports an empty range and we treat it as "nothing structural
 * happened" and skip the rebuild. That's safe only because the things the rebuild actually cares
 * about — the node types and heading `level` that `sectionBoundary` reads — always change through a
 * structural step, never a bare attribute edit. If someone later makes `sectionBoundary` depend on
 * an attribute that *is* changed that way, the decorations will quietly fall out of date; fix it by
 * changing that attribute structurally or by widening this check.
 */
function touchesBlockStructure(tr: Transaction): boolean {
  return tr.steps.some((step, i) => {
    const docBefore = tr.docs[i];
    let structural = false;

    step.getMap().forEach((from, to) => {
      const $from = docBefore.resolve(from);
      if (!$from.parent.isTextblock || to > $from.end()) {
        structural = true;
      }
    });

    return structural;
  });
}
